import useTranslator from "@hooks/use-translator"
import DashboardLayout from "./layout"
import { useInfiniteQuery } from "@tanstack/react-query"
import { useAuth } from "@stores/auth-store"
import { AppNotification, GetActivitiesProps } from "@pma/types"
import { Button } from "@components/common/button"
import { AlertCircle } from "lucide-react"
import PmsLoader from "@components/common/loader"
import ActivityLogCard from "@components/activities/activity-log-card"
import { useEffect } from "react"
import { useLocation, useNavigate, useSearchParams } from "react-router-dom"
import { ACCESS_TOKEN } from "@pma/utils/constants"
import { parseCookies } from "nookies"
import axios from "axios"
import { SERVER_URL } from "@pma/utils/api-routes"

const ActivitiesPage = () => {
  const location = useLocation()
  const navigate = useNavigate()
  const { authConfig } = useAuth()
  const { translate } = useTranslator()
  const [searchParam] = useSearchParams()
  const queryKey = searchParam.get("revalidate")
  const orgId = authConfig?.last_organization_id
  const accessToken = parseCookies()[ACCESS_TOKEN]

  const getActivitiesList = async ({
    paging_start_page,
    paging_per_page,
    organization_id,
  }: GetActivitiesProps) => {
    try {
      const payload = {
        organization_id: organization_id,
        paging_start_page: paging_start_page,
        paging_per_page: paging_per_page,
      }

      const response = await axios.post(SERVER_URL + "/activites/get/byUser", payload, {
        headers: {
          Authorization: "Bearer " + accessToken,
        },
      })
      return response.data
    } catch (error) {
      return error
    }
  }

  const {
    data: activities,
    refetch: refetchLogs,
    isLoading,
    fetchNextPage,
    hasNextPage,
  } = useInfiniteQuery({
    queryKey: ["logs-notifications"],
    queryFn: ({ pageParam = 1 }) =>
      getActivitiesList({
        paging_per_page: 30,
        paging_start_page: pageParam,
        organization_id: orgId,
      }),
    initialPageParam: 1,
    getNextPageParam: (lastPage, allPages) => {
      if (!lastPage?.nextPage) return undefined
      return allPages.length + 1
    },
  })

  useEffect(() => {
    if (queryKey === "tasks") {
      refetchLogs()
      navigate(`${location.pathname}`)
    }
  }, [queryKey])

  return (
    <DashboardLayout>
      <div className='flex min-h-[60vh] w-full flex-col gap-4 pb-12'>
        {isLoading && <PmsLoader />}
        {activities &&
          activities?.pages?.map((page: any) =>
            page?.items?.map((notification: AppNotification, index: number) => (
              <ActivityLogCard
                notification={notification}
                context={"logs"}
                onNotificationClick={refetchLogs}
                key={index}
              />
            )),
          )}

        {!isLoading && !activities?.pages[0]?.pageTotal && (
          <div className='flex h-[90vh] flex-col items-center justify-center rounded-md border border-slate-300 text-2xl'>
            {translate("No items found")}
            <AlertCircle size={40} className='mt-2' />
          </div>
        )}
        {hasNextPage && (
          <Button
            variant={"link"}
            loading={isLoading}
            className='m-2 w-full'
            onClick={() => hasNextPage && fetchNextPage()}
          >
            {translate("Load More")}
          </Button>
        )}
      </div>
    </DashboardLayout>
  )
}

export default ActivitiesPage
